import axios, {
  AxiosError,
  type AxiosInstance,
  type AxiosRequestConfig,
  type AxiosResponse,
  type InternalAxiosRequestConfig,
} from 'axios';
import { env } from '@/core/config/env';

declare module 'axios' {
  export interface AxiosRequestConfig {
    /** Opt out of the automatic `X-Tenant-Id` injection — for user-scoped, non-tenant resources (e.g. Notes). */
    skipTenantHeader?: boolean;
    /**
     * Opt out of the global error toast for this request only. The rejection still propagates,
     * so the caller must handle it. For endpoints where a failure status is part of normal
     * operation and means nothing to the user — e.g. the whiteboard snapshot compaction race,
     * where a 409 only says another client compacted first.
     */
    skipGlobalError?: boolean;
  }
}

export interface IHttpResponse<T> {
  data: T;
  status: number;
}

export interface IHttpClient {
  get<T>(url: string, config?: AxiosRequestConfig): Promise<IHttpResponse<T>>;
  post<T, R>(url: string, data: T, config?: AxiosRequestConfig): Promise<IHttpResponse<R>>;
  put<TBody, TResponse = TBody>(
    url: string,
    data?: TBody,
    config?: AxiosRequestConfig,
  ): Promise<IHttpResponse<TResponse>>;
  patch<TBody, TResponse = TBody>(
    url: string,
    data: TBody,
    config?: AxiosRequestConfig,
  ): Promise<IHttpResponse<TResponse>>;
  delete<T>(url: string, data?: unknown, config?: AxiosRequestConfig): Promise<IHttpResponse<T>>;

  setTokenProvider(
    getToken: () => string | null,
    getActiveOrganizationId?: (() => string | null) | null,
  ): void;
  setRefreshHandler(fn: () => Promise<string>): void;
  setErrorHandler(fn: (error: AxiosError) => void): void;
  setRequestLifecycleHandlers(handlers: {
    onRequestStart?: () => void;
    onRequestEnd?: () => void;
  }): void;
}

interface FailedQueueItem {
  resolve: (token: string) => void;
  reject: (error: AxiosError) => void;
}

export const httpClient = createHttpClient(env.VITE_API_URL);

export function createHttpClient(baseURL: string): IHttpClient {
  const instance: AxiosInstance = axios.create({ baseURL });

  // Dependencias inyectables
  let getToken: (() => string | null) | null = null;
  let getActiveOrganizationId: (() => string | null) | null = null;
  let refreshHandler: (() => Promise<string>) | null = null;
  let globalErrorHandler: ((error: AxiosError) => void) | null = null;

  let onRequestStart: (() => void) | undefined;
  let onRequestEnd: (() => void) | undefined;

  // Estado interno encapsulado
  let isRefreshing = false;
  let failedQueue: FailedQueueItem[] = [];

  const processQueue = (error: AxiosError | null, token?: string) => {
    failedQueue.forEach(({ resolve, reject }) => {
      if (error) reject(error);
      else resolve(token!);
    });
    failedQueue = [];
  };

  // ===============================
  // REQUEST INTERCEPTOR
  // ===============================
  instance.interceptors.request.use(
    (config: InternalAxiosRequestConfig) => {
      onRequestStart?.();

      if (getToken && !config.headers.Authorization) {
        const token = getToken();
        if (token) {
          config.headers.Authorization = `Bearer ${token}`;
        }
      }
      if (getActiveOrganizationId && !config.skipTenantHeader) {
        const orgId = getActiveOrganizationId();
        if (orgId) {
          config.headers['X-Tenant-Id'] = orgId;
        }
      }

      const method = config.method?.toUpperCase();
      if (
        (method === 'POST' || method === 'PUT' || method === 'PATCH') &&
        !config.headers['X-Idempotency-Key']
      ) {
        config.headers['X-Idempotency-Key'] = crypto.randomUUID();
      }

      return config;
    },
    (error: AxiosError) => {
      onRequestEnd?.();
      return Promise.reject(error);
    },
  );

  // ===============================
  // RESPONSE INTERCEPTOR
  // ===============================
  instance.interceptors.response.use(
    (response: AxiosResponse) => {
      onRequestEnd?.();
      return response;
    },
    async (error: AxiosError) => {
      onRequestEnd?.();

      const originalRequest = error.config as AxiosRequestConfig & {
        _retry?: boolean;
      };

      // Si no hay refresh configurado → error directo
      if (!refreshHandler) {
        globalErrorHandler?.(error);
        return Promise.reject(error);
      }

      if (error.response?.status === 401 && !originalRequest._retry) {
        originalRequest._retry = true;

        if (isRefreshing) {
          return new Promise<AxiosResponse>((resolve, reject) => {
            failedQueue.push({
              resolve: (token: string) => {
                originalRequest.headers = originalRequest.headers || {};
                originalRequest.headers.Authorization = `Bearer ${token}`;
                resolve(instance(originalRequest));
              },
              reject,
            });
          });
        }

        isRefreshing = true;

        try {
          const newToken = await refreshHandler();

          processQueue(null, newToken);

          originalRequest.headers = originalRequest.headers || {};
          originalRequest.headers.Authorization = `Bearer ${newToken}`;

          return instance(originalRequest);
        } catch (refreshError) {
          processQueue(refreshError as AxiosError);
          globalErrorHandler?.(refreshError as AxiosError);
          return Promise.reject(refreshError);
        } finally {
          isRefreshing = false;
        }
      }

      if (!(error.config as AxiosRequestConfig | undefined)?.skipGlobalError) {
        globalErrorHandler?.(error);
      }
      return Promise.reject(error);
    },
  );

  // ===============================
  // Métodos HTTP
  // ===============================

  const get = async <T>(url: string, config?: AxiosRequestConfig): Promise<IHttpResponse<T>> => {
    const response = await instance.get<T>(url, config);
    return { data: response.data, status: response.status };
  };

  const post = async <T, R>(
    url: string,
    data: T,
    config?: AxiosRequestConfig,
  ): Promise<IHttpResponse<R>> => {
    const response = await instance.post<R>(url, data, config);
    return { data: response.data, status: response.status };
  };

  const put = async <TBody, TResponse = TBody>(
    url: string,
    data?: TBody,
    config?: AxiosRequestConfig,
  ): Promise<IHttpResponse<TResponse>> => {
    const response = await instance.put<TResponse>(url, data, config);
    return { data: response.data, status: response.status };
  };

  const patch = async <TBody, TResponse = TBody>(
    url: string,
    data: TBody,
    config?: AxiosRequestConfig,
  ): Promise<IHttpResponse<TResponse>> => {
    const response = await instance.patch<TResponse>(url, data, config);
    return { data: response.data, status: response.status };
  };

  const remove = async <T>(
    url: string,
    data?: unknown,
    config?: AxiosRequestConfig,
  ): Promise<IHttpResponse<T>> => {
    const response = await instance.delete<T>(url, {
      ...config,
      data,
    });
    return { data: response.data, status: response.status };
  };

  return {
    get,
    post,
    put,
    patch,
    delete: remove,

    setTokenProvider: (getTokenFn, getActiveOrganizationIdFn) => {
      getToken = getTokenFn;
      getActiveOrganizationId = getActiveOrganizationIdFn ?? null;
    },
    setRefreshHandler: (fn) => (refreshHandler = fn),
    setErrorHandler: (fn) => (globalErrorHandler = fn),
    setRequestLifecycleHandlers: (handlers) => {
      onRequestStart = handlers.onRequestStart;
      onRequestEnd = handlers.onRequestEnd;
    },
  };
}
